import { useEffect, useState } from 'react';
import { useAllianceStore } from '../stores/allianceStore';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RequestAlliance from '../components/alliances/RequestAlliance';
import ActiveAlliances from '../components/alliances/ActiveAlliances';
import AllianceRequests from '../components/alliances/AllianceRequests';
import RequestedAlliances from '../components/alliances/RequestedAlliances';
import { useProfileStore } from '../stores/profileStore';
import {
  getActiveAlliances,
  getOutgoingPendingRequests,
  getAllianceUserIdsToIgnore,
} from '../utils/allianceUtils';
import { getIncomingPendingRequests } from '../utils/allianceUtils';
import { cleanupModalParamsFromUrl, decodeModalParams } from '../utils/modalUrlParams';
import AllianceRequestModal from '../components/alliances/AllianceRequestModal';
import { useFreelancerProfileStore } from '../stores/freelancerProfileStore';
import type { FreelancerSearchResult } from '../types/search';

const Alliances = () => {
  const { t } = useTranslation('alliances');
  const { profile } = useProfileStore();
  const { loading, alliances, fetchAlliances } = useAllianceStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetch_freelancer_profile, freelancerProfiles } = useFreelancerProfileStore();
  const [restoredFreelancer, setRestoredFreelancer] = useState<FreelancerSearchResult | null>(null);

  useEffect(() => {
    fetchAlliances();
  }, [fetchAlliances]);

  // Restore modal from URL params after Stripe redirect
  useEffect(() => {
    const modalParams = decodeModalParams(searchParams);
    if (modalParams?.type === 'allianceRequest' && modalParams.freelancerId) {
      const profile = freelancerProfiles[modalParams.freelancerId];
      if (profile && !restoredFreelancer) {
        // Convert FreelancerProfile to FreelancerSearchResult
        const searchResult: FreelancerSearchResult = {
          id: profile.id,
          first_name: profile.first_name,
          last_name: profile.last_name ?? null,
          job_title: profile.job_title,
          service_category_slugs: profile.services?.map(s => s.slug) || [],
          avatar_url: profile.avatar_url,
          bio: profile.bio,
          city: profile.city?.name || null,
          alliance_count: profile.stats?.alliance_count || 0,
          recent_alliances: profile.recent_alliances || null,
          full_access: profile.full_access,
          alliance_status: profile.alliance_status || 'none',
          overall_rating: profile.stats?.overall_rating || 0,
          reviews_count: profile.stats?.reviews_count || 0,
          projects_count: profile.stats?.projects_count || 0,
        };
        setRestoredFreelancer(searchResult);
      } else if (!profile) {
        // Fetch the profile if not cached
        fetch_freelancer_profile(modalParams.freelancerId);
      }
    }
  }, [freelancerProfiles, searchParams, restoredFreelancer, fetch_freelancer_profile]);

  const incomingPendingRequests = getIncomingPendingRequests(alliances, profile?.id ?? '');
  const outgoingPendingRequests = getOutgoingPendingRequests(alliances, profile?.id ?? '');
  const activeAlliances = getActiveAlliances(alliances, profile?.id ?? '');
  const userIdsToIgnore = getAllianceUserIdsToIgnore(alliances);

  const navigate = useNavigate();

  return (
    <div className="mb-8 flex flex-col md:min-h-[calc(100vh-4rem)] md:px-5 lg:px-32">
      <div className="flex w-full flex-col pb-5 pt-4 md:w-2/3 md:px-1 md:pb-8 md:pr-8">
        <h1 className="text-5xl font-bold text-secondary">{t('title')}</h1>
      </div>

      <AllianceRequests pendingRequests={incomingPendingRequests} loading={loading} />
      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        <ActiveAlliances activeAlliances={activeAlliances} loading={loading} />

        <div className="flex flex-col gap-6 md:max-w-[35rem] md:gap-8">
          <RequestAlliance userIdsToIgnore={userIdsToIgnore} />
          <RequestedAlliances alliances={outgoingPendingRequests} loading={loading} />
        </div>
      </div>

      {restoredFreelancer && (
        <AllianceRequestModal
          freelancer={restoredFreelancer}
          onClose={() => {
            setRestoredFreelancer(null);
            cleanupModalParamsFromUrl(searchParams, setSearchParams);
            navigate(location.pathname, { replace: true });
          }}
        />
      )}
    </div>
  );
};

export default Alliances;
